import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './UsersPage.module.css';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

export function UsersPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<User[]>('/auth/users');
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError(response.error || 'Failed to load users');
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError(null);

    try {
      const response = await api.post('/auth/create-admin', {
        email: newUserEmail,
        name: newUserName,
        password: newUserPassword,
      });

      if (response.success) {
        setShowCreateForm(false);
        setNewUserEmail('');
        setNewUserName('');
        setNewUserPassword('');
        await loadUsers();
      } else {
        setError(response.error || 'Failed to create user');
      }
    } catch (err) {
      setError('Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleActive = async (userId: number) => {
    try {
      const response = await api.post(`/auth/users/${userId}/toggle-active`);
      if (response.success) {
        await loadUsers();
      } else {
        setError(response.error || 'Failed to update user status');
      }
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>User Management</h1>
          <p className={styles.subtitle}>Manage system users</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Add User'}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Create User Form */}
      {showCreateForm && (
        <div className={styles.createForm}>
          <h2>Create New User</h2>
          <form onSubmit={handleCreateUser}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                placeholder="user@example.com"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                required
                placeholder="John Doe"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                required
                placeholder="Min 8 chars, uppercase, lowercase, number, special"
              />
              <span className={styles.hint}>
                Must include: uppercase, lowercase, number, special character
              </span>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {/* Users List */}
      {isLoading ? (
        <div className={styles.loading}>
          <div className="spinner" />
          <span>Loading users...</span>
        </div>
      ) : users.length === 0 ? (
        <div className={styles.empty}>
          <p>No users found</p>
        </div>
      ) : (
        <div className={styles.userList}>
          {users.map((user) => (
            <div
              key={user.id}
              className={`${styles.userCard} ${!user.is_active ? styles.inactive : ''}`}
            >
              <div className={styles.userInfo}>
                <div className={styles.userName}>{user.name}</div>
                <div className={styles.userEmail}>{user.email}</div>
                <div className={styles.userMeta}>
                  <span className={`badge badge-${user.role === 'admin' ? 'primary' : 'secondary'}`}>
                    {user.role}
                  </span>
                  <span>Joined {formatDate(user.created_at)}</span>
                </div>
              </div>
              <div className={styles.userActions}>
                <span className={`badge badge-${user.is_active ? 'success' : 'error'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                <button
                  className={`btn btn-sm ${user.is_active ? 'btn-outline' : 'btn-primary'}`}
                  onClick={() => handleToggleActive(user.id)}
                >
                  {user.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
